import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { usePersistentState } from './viewState'

/**
 * Remembers which view you were last on.
 *
 * Only real pages are remembered, never the entry route: that is where the
 * decision to go back is made (EntryRoute), and saving it first would replace
 * the page being returned to before anyone read it.
 */

const LAST_ROUTE = 'last-route'

// An imported session can carry anything; only an address inside the app is followed.
// The entry route itself is not a page to return to.
const isAppPath = (value: unknown) =>
  value === null || (typeof value === 'string' && /^\/[^/]/.test(value))

export function RouteMemory({ enabled }: { enabled: boolean }) {
  const location = useLocation()
  const [stored, setStored, restored] = usePersistentState<string | null>(LAST_ROUTE, null, {
    validate: isAppPath,
  })

  useEffect(() => {
    if (!enabled || !restored || location.pathname === '/' || location.pathname === '') return
    const path = location.pathname + location.search
    if (path !== stored) setStored(path)
  }, [enabled, restored, location.pathname, location.search, stored, setStored])

  return null
}

/**
 * What the entry route shows: the page you were last on, or the overview.
 *
 * Decided here alone. A plain redirect to the overview beside a separate
 * restore ran first, and the restore then found itself no longer at the
 * entry route and did nothing.
 *
 * @param waiting True while the session is still being read; nothing is
 * decided until it has been.
 * @param resume Whether returning to the last page makes sense: without
 * data, every page but the overview is the welcome page anyway.
 */
export function EntryRoute({ waiting, resume }: { waiting: boolean; resume: boolean }) {
  const [stored, , restored] = usePersistentState<string | null>(LAST_ROUTE, null, {
    validate: isAppPath,
  })
  if (waiting || !restored) return null
  // With nothing loaded, the analysis pages have nothing to show: start where
  // something can be loaded.
  return <Navigate to={resume ? stored || '/assessment' : '/welcome'} replace />
}
